'use client';

import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { AuthErrorBoundary } from '@/components/auth/auth-error-boundary';
import { AuthLoading } from '@/components/auth/auth-loading';

import type { User } from 'next-auth';
import { getSession } from 'next-auth/react';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAuth?: boolean;
  requireSubscription?: boolean;
  redirectTo?: string;
  loadingComponent?: React.ReactNode;
}

interface SubscriptionData {
  status: string | null;
  price_id: string | null;
  current_period_end: string | null;
}

export function AuthGuard({
  children,
  fallback,
  requireAuth = true,
  requireSubscription = false,
  redirectTo = '/auth/signin',
  loadingComponent
}: AuthGuardProps) {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAuthAndSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check authentication
      const session = await getSession();
      const currentUser = session?.user || null;
      setUser(currentUser);

      // If auth is required but user is not authenticated
      if (requireAuth && !currentUser) {
        router.push(redirectTo);
        return;
      }

      // If subscription is required, check subscription status
      if (requireSubscription) {
        const response = await fetch('/api/subscription');
        if (response.ok) {
          const data = await response.json();
          setSubscription(data.subscription);
        } else {
          setSubscription(null);
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }, [requireAuth, requireSubscription, redirectTo, router]);

  // Computed values
  const _isAuthenticated = !!user;
  const _hasActiveSubscription =
    subscription?.status === 'active' || subscription?.status === 'trialing';

  useEffect(() => {
    checkAuthAndSubscription();
  }, [checkAuthAndSubscription]);

  // Show loading state
  if (loading) {
    return loadingComponent || <AuthLoading message='Checking access...' />;
  }

  // Show error state
  if (error && requireAuth) {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <div className='text-center'>
          <p className='mb-4 text-red-600 dark:text-red-400'>{error}</p>
          <button
            type='button'
            onClick={checkAuthAndSubscription}
            className='text-indigo-600 hover:text-indigo-500'>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If user doesn't have required subscription, show fallback
  if (requireSubscription && subscription) {
    const hasActiveSubscription =
      subscription.status === 'active' || subscription.status === 'trialing';

    if (!hasActiveSubscription && fallback) {
      return <>{fallback}</>;
    }
  }

  // If user doesn't meet requirements and no fallback provided, don't render children
  if ((requireAuth && !user) || (requireSubscription && !subscription)) {
    return null;
  }

  // All checks passed, render children
  return <>{children}</>;
}

// Hook for checking auth status in components
export function useAuthGuard() {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  const checkAccess = useCallback(async () => {
    try {
      setLoading(true);
      const session = await getSession();
      const currentUser = session?.user || null;
      setUser(currentUser);

      if (currentUser) {
        const response = await fetch('/api/subscription');
        if (response.ok) {
          const data = await response.json();
          setSubscription(data.subscription);
        } else {
          setSubscription(null);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const hasActiveSubscription =
    subscription?.status === 'active' || subscription?.status === 'trialing';

  return {
    user,
    subscription,
    loading,
    hasActiveSubscription,
    isAuthenticated: !!user,
    checkAccess
  };
}

// Premium content wrapper component
export function PremiumContent({
  children,
  fallback,
  title = 'Premium Content',
  description = 'Subscribe to access this exclusive content'
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  title?: string;
  description?: string;
}) {
  return (
    <AuthGuard
      requireSubscription={true}
      fallback={
        fallback || (
          <div className='my-6 rounded-lg border-2 border-amber-200 bg-linear-to-r from-amber-50 to-orange-50 p-6 dark:border-amber-800 dark:from-amber-900/20 dark:to-orange-900/20'>
            <div className='text-center'>
              <div className='mb-4 text-4xl'>🔒</div>
              <h3 className='text-foreground mb-2 text-xl font-bold'>
                {title}
              </h3>
              <p className='text-muted-foreground mb-4'>{description}</p>
              <a
                href='/pricing'
                className='inline-flex items-center rounded-lg bg-amber-600 px-4 py-2 font-medium text-white transition-colors hover:bg-amber-700'>
                Upgrade to Premium
              </a>
            </div>
          </div>
        )
      }>
      {children}
    </AuthGuard>
  );
}
